import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wand2, Sparkles, FolderKanban, Gauge, Wallet, Image as ImageIcon, Video, FolderOpen, CalendarClock } from "lucide-react";
import api from "@/lib/api";

const fmtDay = (d) => (d ? new Date(d).toLocaleDateString("ar-SA-u-ca-gregory-nu-latn", { day: "numeric", month: "short", year: "numeric" }) : "");

// عدّادات صغيرة لكل نوع — تضغط عليها لعرض نوعها بالأسفل
export function Counters({ sections, groups, active, onPick }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid="ws-counters">
      {sections.map((s) => {
        const Icon = s.icon;
        const on = s.id === active;
        return (
          <button key={s.id} type="button" onClick={() => onPick(s.id)} data-testid={`ws-counter-${s.id}`}
            className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-full border text-xs font-bold transition-colors ${on ? "border-primary bg-primary text-white" : "border-border bg-card hover:border-primary/50"}`}>
            <Icon className="w-3.5 h-3.5" />
            <span>{s.label}</span>
            <span className={`min-w-5 h-5 px-1.5 rounded-full grid place-items-center text-[11px] ${on ? "bg-white/20" : "bg-secondary"}`} data-testid={`ws-count-${s.id}`}>{groups[s.id]?.length || 0}</span>
          </button>
        );
      })}
    </div>
  );
}

const PATHS = [
  { id: "studio", label: "توليد فردي", icon: Wand2, tone: "text-primary bg-primary/10 border-primary/30", go: (nav) => nav("/app/studio?individual=1") },
  { id: "strategy", label: "مشروع استراتيجية", icon: Sparkles, tone: "text-accent bg-accent/10 border-accent/30", go: (nav) => nav("/app/projects", { state: { create: "strategy" } }) },
  { id: "content", label: "مشروع محتوى", icon: FolderKanban, tone: "text-emerald-500 bg-emerald-500/10 border-emerald-500/30", go: (nav) => nav("/app/projects", { state: { create: "content" } }) },
];

// ابدأ هنا: ثلاثة مسارات فقط — أيقونات مربعة صغيرة متجاورة
export function StartHere() {
  const navigate = useNavigate();
  return (
    <div data-testid="ws-start-here">
      <div className="text-xs font-bold text-muted-foreground mb-2">ابدأ هنا</div>
      <div className="flex gap-2">
        {PATHS.map((p) => (
          <button key={p.id} type="button" onClick={() => p.go(navigate)} data-testid={`ws-path-${p.id}`}
            className={`w-24 h-24 sm:w-28 sm:h-28 rounded-2xl border flex flex-col items-center justify-center gap-2 text-center transition-transform hover:-translate-y-0.5 ${p.tone}`}>
            <p.icon className="w-6 h-6" />
            <span className="text-[11px] sm:text-xs font-bold leading-tight px-1 text-foreground">{p.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// الأرصدة + الباقات المفعّلة وتاريخ انتهائها — سطر مضغوط
export function BalancesStrip() {
  const navigate = useNavigate();
  const [usage, setUsage] = useState(null);
  const [wallet, setWallet] = useState(null);
  useEffect(() => {
    const load = () => { api.get("/usage").then((r) => setUsage(r.data)).catch(() => {}); api.get("/wallet").then((r) => setWallet(r.data)).catch(() => {}); };
    load();
    window.addEventListener("rawaj:usage-changed", load);
    return () => window.removeEventListener("rawaj:usage-changed", load);
  }, []);
  if (!usage) return null;
  const plan = usage.plan || {};
  const lim = plan.limits || {};
  const g = usage.grants || {};
  const unlimited = (k) => lim[k] == null || lim[k] < 0;
  const left = (k, used) => (unlimited(k) ? "غير محدود" : Math.max(0, lim[k] - (used || 0)));
  const sub = (k, used, extra) => {
    const parts = [];
    if (!unlimited(k)) parts.push(`${Math.min(used || 0, lim[k])}/${lim[k]} مستخدم`);
    if (extra > 0) parts.push(`+${extra} رصيد إضافي`);
    return parts.join(" · ");
  };
  const chips = [
    { icon: FolderOpen, label: "مشاريع متبقية", val: left("projects", usage.usage?.projects), sub: sub("projects", usage.usage?.projects, 0), tid: "projects" },
    { icon: ImageIcon, label: "صور متبقية", val: left("images", usage.usage?.images), sub: sub("images", usage.usage?.images, g.images_left), tid: "images" },
    { icon: Video, label: "فيديو متبقٍ", val: left("videos", usage.usage?.videos), sub: sub("videos", usage.usage?.videos, g.videos_left), tid: "videos" },
    { icon: Wallet, label: "رصيد المحفظة", val: `${wallet?.balance ?? 0} ر.س`, sub: "لشراء رصيد إضافي", tid: "wallet" },
  ];
  return (
    <div className="glass rounded-2xl border border-border p-3 sm:p-4" data-testid="ws-balances">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Gauge className="w-4 h-4 text-primary" />
        <span className="text-sm font-bold">الأرصدة والباقات</span>
        <span className="text-xs bg-primary/15 text-primary px-2.5 py-1 rounded-full" data-testid="ws-plan-name">الباقة المفعّلة: {plan.name || "—"}</span>
        {usage.plan_expires_at && (
          <span className="text-xs inline-flex items-center gap-1 bg-secondary px-2.5 py-1 rounded-full" data-testid="ws-plan-expiry"><CalendarClock className="w-3.5 h-3.5" /> تنتهي {fmtDay(usage.plan_expires_at)}</span>
        )}
        {(usage.grants?.videos_left > 0 || usage.grants?.images_left > 0) && (
          <span className="text-xs bg-accent/15 text-accent border border-accent/30 px-2.5 py-1 rounded-full font-bold" data-testid="ws-grants">هدية: {usage.grants.videos_left} فيديو · {usage.grants.images_left} صورة</span>
        )}
        <button type="button" onClick={() => navigate("/app/packages")} className="ms-auto text-xs font-bold text-accent hover:underline" data-testid="ws-upgrade">ترقية / شحن ←</button>
      </div>
      <div className="grid grid-cols-4 gap-2">
        {chips.map((c) => (
          <div key={c.tid} className="rounded-xl bg-secondary/60 border border-border p-2 text-center" data-testid={`ws-balance-${c.tid}`}>
            <c.icon className="w-4 h-4 mx-auto text-primary mb-1" />
            <div className="text-sm font-black leading-none">{c.val}</div>
            <div className="text-[10px] text-muted-foreground mt-1">{c.label}</div>
            {c.sub && <div className="text-[10px] text-accent mt-0.5 leading-tight" data-testid={`ws-balance-${c.tid}-sub`}>{c.sub}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
